import FormModalShell from "../../../shared/FormModalShell";
import useFormModal from "../../../../hooks/ui/useFormModal";

const ACTION_STYLE = {
  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
  boxShadow: '0 10px 25px rgba(34, 197, 94, 0.4)',
};

const INITIAL = { groupName: '', professeur: '', matiere: '' };

// Un groupe de besoin porte sur au moins deux classes (règle du back, décision
// du 28/09/2026) : créé ici avec une seule classe, il serait un demi-groupe et
// disparaîtrait de « Configuration des groupes » à la seconde même. Les groupes
// d'une seule classe viennent de l'ENT.
export const NB_CLASSES_MIN = 2;
export const MESSAGE_DEUX_CLASSES = "Un groupe de besoin regroupe au moins deux classes (une barrette). Les groupes d'une seule classe viennent de l'ENT et ne se créent pas ici.";

const AddGroupe = ({ show, handleClose, handleAddGroupe, profDetail, listClasses, selectedClasses, handleCheckClasseChange, niveau, handleRadioChange, isDelegueAffectation }) => {

  const nbClasses = selectedClasses?.length || 0;

  const validate = (v) => {
    const e = {};
    if (!v.groupName.trim()) e.groupName = 'Le nom du groupe est requis';
    if (isDelegueAffectation && !v.matiere) e.matiere = 'Veuillez sélectionner une matière';
    if (!v.professeur) e.professeur = 'Le professeur référent est requis';
    if (nbClasses < NB_CLASSES_MIN) e.classes = MESSAGE_DEUX_CLASSES;
    return e;
  };

  // selectedMatiere (v.matiere) passée en 2ème param : signal local pour la
  // vérification de cohérence côté parent (ne fait pas partie du payload backend).
  const onSubmit = (v) => handleAddGroupe(
    { nom: v.groupName, professeur: parseInt(v.professeur, 10) },
    v.matiere,
  );

  const { values, setValues, errors, setErrors, handleChange, handleSubmit, handleKeyDown, isSubmitting, modalRef, firstFieldRef } =
    useFormModal({ show, initialValues: INITIAL, validate, onSubmit, onClose: handleClose });

  return (
    <FormModalShell
      show={show}
      onClose={handleClose}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      modalRef={modalRef}
      onKeyDown={handleKeyDown}
      title="📚 Créer un groupe"
      subtitle="Nouveau groupe de besoins"
      actionLabel="✓ Créer le groupe"
      submittingLabel="Création en cours..."
      actionStyle={ACTION_STYLE}
      // Grisé tant que la barrette n'a pas deux classes (la phrase sous les cases dit pourquoi).
      actionDisabled={nbClasses < NB_CLASSES_MIN}
      cardClassName="max-w-md xl:max-w-sm 2xl:max-w-md"
      headerClassName="p-8 xl:p-5 2xl:p-6 3xl:p-8"
      bodyClassName="p-8 xl:p-5 2xl:p-6 3xl:p-8 space-y-6 xl:space-y-4 2xl:space-y-5 3xl:space-y-6"
      titleClassName="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-semibold mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2"
      subtitleClassName="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm"
      buttonClassName="px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm xl:text-xs 2xl:text-sm 3xl:text-sm"
    >
      {/* Erreur générale */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
          {errors.submit}
        </div>
      )}

      {/* Champ Nom du groupe */}
      <div className="space-y-2">
        <label htmlFor="groupName" className="block text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700">
          Nom du groupe
        </label>
        <input
          ref={firstFieldRef}
          type="text"
          id="groupName"
          name="groupName"
          value={values.groupName}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.ctrlKey) {
              e.preventDefault();
              document.getElementById('professeur')?.focus();
            }
          }}
          disabled={isSubmitting}
          className={`
            w-full px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 rounded-xl
            transition-all duration-200 ease-out
            focus:outline-none focus:ring-0 text-sm xl:text-xs 2xl:text-sm 3xl:text-sm
            ${errors.groupName
              ? 'border-red-300 bg-red-50 focus:border-red-500'
              : 'border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/10'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          placeholder="Nom du groupe"
        />
        {errors.groupName && (
          <div className="bg-orange-50 border text-sm xl:text-xs 2xl:text-xs 3xl:text-sm border-red-200 rounded-xl p-2 xl:p-1.5 2xl:p-2 3xl:p-2 mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
            <p className="text-red-700 font-medium">{errors.groupName}</p>
          </div>
        )}
      </div>

      {/* Sélection Niveau */}
      <div className="space-y-2">
        <label className="block text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700">
          Niveau scolaire
        </label>
        <div className="flex gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="niveau"
              value="6eme"
              checked={niveau === "6eme"}
              onChange={handleRadioChange}
              disabled={isSubmitting}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">6ème</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="niveau"
              value="5eme"
              checked={niveau === "5eme"}
              onChange={handleRadioChange}
              disabled={isSubmitting}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">5ème</span>
          </label>
        </div>
      </div>

      {/* Sélection Matière — uniquement pour le prof délégué (qui peut créer dans les 2 matières) */}
      {isDelegueAffectation && (
        <div className="space-y-2">
          <label className="block text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700">
            Matière
          </label>
          <div className="flex gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
            {[
              { value: 'maths', label: 'Maths' },
              { value: 'Français', label: 'Français' },
            ].map((m) => (
              <label key={m.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="matiere"
                  value={m.value}
                  checked={values.matiere === m.value}
                  onChange={(e) => {
                    setValues((prev) => ({ ...prev, matiere: e.target.value, professeur: '' }));
                    if (errors.matiere) setErrors((prev) => ({ ...prev, matiere: '' }));
                  }}
                  disabled={isSubmitting}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">{m.label}</span>
              </label>
            ))}
          </div>
          {errors.matiere && (
            <div className="bg-orange-50 border text-sm xl:text-xs 2xl:text-xs 3xl:text-sm border-red-200 rounded-xl p-2 xl:p-1.5 2xl:p-2 3xl:p-2">
              <p className="text-red-700 font-medium">{errors.matiere}</p>
            </div>
          )}
        </div>
      )}

      {/* Sélection des Classes (Barrettes) */}
      <div className="space-y-2">
        <label className="block text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700">
          Classes (Barrettes)
        </label>
        <div className="max-h-32 xl:max-h-24 2xl:max-h-28 3xl:max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3 xl:p-2 2xl:p-2.5 3xl:p-3 space-y-2 xl:space-y-1.5 2xl:space-y-1.5 3xl:space-y-2">
          {listClasses && listClasses
            .filter(classe => classe.nom[0] === niveau[0])
            .map((classe) => (
            <label key={classe.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
              <input
                type="checkbox"
                checked={selectedClasses?.includes(classe.id) || false}
                onChange={() => handleCheckClasseChange(classe.id)}
                disabled={isSubmitting}
                className="text-blue-600 focus:ring-blue-500 rounded"
              />
              <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">{classe.nom}</span>
            </label>
          ))}
        </div>
        {errors.classes ? (
          <div className="bg-orange-50 border text-sm xl:text-xs 2xl:text-xs 3xl:text-sm border-red-200 rounded-xl p-2 xl:p-1.5 2xl:p-2 3xl:p-2">
            <p className="text-red-700 font-medium">{errors.classes}</p>
          </div>
        ) : nbClasses < NB_CLASSES_MIN ? (
          <p className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-gray-600">
            {MESSAGE_DEUX_CLASSES}
          </p>
        ) : null}
        {nbClasses > 0 && (
          <div className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-blue-600">
            {nbClasses} classe(s) sélectionnée(s)
          </div>
        )}
      </div>

      {/* Professeur référent */}
      <div className="space-y-2">
        <label htmlFor="professeur" className="block text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700">
          Professeur référent
        </label>
        <select
          id="professeur"
          name="professeur"
          value={values.professeur}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.ctrlKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          disabled={isSubmitting}
          className={`
            w-full px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 rounded-xl
            transition-all duration-200 ease-out text-sm xl:text-xs 2xl:text-sm 3xl:text-sm
            focus:outline-none focus:ring-0 cursor-pointer
            ${errors.professeur
              ? 'border-red-300 bg-red-50 focus:border-red-500'
              : 'border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/10'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <option value="">
            {isDelegueAffectation && !values.matiere
              ? "Choisir d'abord la matière"
              : "Sélectionner un professeur"}
          </option>
          {profDetail && profDetail
            .filter(prof => isDelegueAffectation ? prof.matiere === values.matiere : true)
            .map((prof) => (
              <option key={prof.id} value={prof.id}>
                {prof.prenom} {prof.nom}
              </option>
            ))}
        </select>
        {errors.professeur && (
          <div className="bg-orange-50 border text-sm xl:text-xs 2xl:text-xs 3xl:text-sm border-red-200 rounded-xl p-2 xl:p-1.5 2xl:p-2 3xl:p-2 mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
            <p className="text-red-700 font-medium">{errors.professeur}</p>
          </div>
        )}
      </div>

      {/* Info raccourci clavier */}
      <div className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-gray-500 text-center">
        Astuce : Appuyez sur Entrée pour passer au champ suivant, Ctrl+Entrée pour valider
      </div>
    </FormModalShell>
  );
};

export default AddGroupe;
